import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../services/api';

const prioridadConfig = {
  alta: { color: 'bg-red-100 text-red-800 border-red-300', dot: 'bg-red-500', label: 'Alta' },
  media: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', dot: 'bg-yellow-500', label: 'Media' },
  baja: { color: 'bg-green-100 text-green-800 border-green-300', dot: 'bg-green-500', label: 'Baja' },
};

const tipoIcons = {
  servicio_pendiente: '&#x1F4C5;',
  tacto_pendiente: '&#x1F50D;',
  destete_pendiente: '&#x1F37C;',
  peso_bajo_servicio: '&#x26A0;',
  control_macho_pendiente: '&#x1F402;',
  ep_baja: '&#x1F4C9;',
  riesgo_descarte: '&#x274C;',
};

const AlertasPage = () => {
  const { user } = useAuth();
  const [alertas, setAlertas] = useState([]);
  const [resumen, setResumen] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todas');
  const [generating, setGenerating] = useState(false);

  const loadAlertas = useCallback(async () => {
    if (!user?.establecimiento_id) return;
    try {
      setLoading(true);
      const prioridad = filtro !== 'todas' ? filtro : undefined;
      const res = await dashboardAPI.getAlertas(user.establecimiento_id, 100, prioridad);
      setAlertas(res.data.alertas || []);
      setResumen(res.data.resumen || {});
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [user, filtro]);

  useEffect(() => { loadAlertas(); }, [loadAlertas]);

  const handleGenerar = async () => {
    try {
      setGenerating(true);
      await dashboardAPI.generarAlertas(user.establecimiento_id);
      await loadAlertas();
    } catch { /* silent */ } finally {
      setGenerating(false);
    }
  };

  const handleResolver = async (alertaId) => {
    try {
      await dashboardAPI.resolverAlerta(alertaId);
      await loadAlertas();
    } catch { /* silent */ }
  };

  const filtros = [
    { id: 'todas', label: 'Todas', count: resumen.total_pendientes || 0 },
    { id: 'alta', label: 'Alta', count: resumen.alta || 0 },
    { id: 'media', label: 'Media', count: resumen.media || 0 },
    { id: 'baja', label: 'Baja', count: resumen.baja || 0 },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="alertas-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Alertas del Sistema</h2>
          <p className="text-sm text-gray-500 mt-1">{resumen.total_pendientes || 0} alertas pendientes</p>
        </div>
        <button
          onClick={handleGenerar}
          disabled={generating}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm disabled:opacity-50"
          data-testid="generate-alerts-btn"
        >
          {generating ? 'Escaneando...' : 'Escanear Rodeo'}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-4 text-center border-l-4 border-gray-400">
          <p className="text-3xl font-bold text-gray-900">{resumen.total_pendientes || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Total Pendientes</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 text-center border-l-4 border-red-500">
          <p className="text-3xl font-bold text-red-600">{resumen.alta || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Prioridad Alta</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 text-center border-l-4 border-yellow-500">
          <p className="text-3xl font-bold text-yellow-600">{resumen.media || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Prioridad Media</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4 text-center border-l-4 border-green-500">
          <p className="text-3xl font-bold text-green-600">{resumen.baja || 0}</p>
          <p className="text-xs text-gray-500 mt-1">Prioridad Baja</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex bg-gray-100 rounded-lg p-1 gap-1 overflow-x-auto">
        {filtros.map((f) => (
          <button
            key={f.id}
            onClick={() => setFiltro(f.id)}
            className={`px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-md transition-colors whitespace-nowrap ${filtro === f.id ? 'bg-white text-green-700 font-semibold shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`filter-${f.id}`}
          >
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      {/* Alerts List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : alertas.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <p className="text-gray-500 text-lg">Sin alertas pendientes</p>
          <p className="text-gray-400 text-sm mt-1">Ejecute "Escanear Rodeo" para generar alertas automaticas</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alertas.map((alerta) => {
            const cfg = prioridadConfig[alerta.prioridad] || prioridadConfig.baja;
            return (
              <div
                key={alerta.id}
                className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${cfg.color.includes('red') ? 'border-red-500' : cfg.color.includes('yellow') ? 'border-yellow-500' : 'border-green-500'} hover:shadow-md transition`}
                data-testid={`alert-${alerta.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <span className={`mt-1 w-2.5 h-2.5 rounded-full ${cfg.dot} flex-shrink-0`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cfg.color}`}>{cfg.label}</span>
                        <span className="text-xs text-gray-400 font-mono">{alerta.tipo?.replace(/_/g, ' ')}</span>
                      </div>
                      <p className="text-sm text-gray-800">{alerta.descripcion}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {alerta.fecha_creacion ? new Date(alerta.fecha_creacion).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleResolver(alerta.id)}
                    className="ml-3 px-3 py-1 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100 transition flex-shrink-0"
                    data-testid={`resolve-alert-${alerta.id}`}
                  >
                    Resolver
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AlertasPage;
